import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useActiveLanguages } from '@/hooks/useActiveLanguages';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api';

/**
 * T1809 — zaporedna številka zadnjega zahtevka za shranjevanje jezika. Ob hitrem
 * klikanju (SL → EN → DE) je v letu več `PATCH`-ov in odgovori lahko prispejo
 * v drugem vrstnem redu; v shrambo se zapiše samo odgovor ZADNJEGA klika, sicer
 * bi profil pokazal jezik, ki ga uporabnik ni izbral nazadnje.
 */
let _persistSeq = 0;

/**
 * T2979 — jezik, ki ga zahteva zadnji še leteči `PATCH` (`null`, ko ni nobenega).
 * Preskok »jezik je že shranjen« mora primerjati s tem, ne s shrambo: ob
 * SL → EN → SL shramba med letom `PATCH en` še kaže `sl`, zato je bila
 * vrnitev na SL preskočena, nato je prispel `en` in obveljal v bazi.
 */
let _pendingLanguage: string | null = null;

/**
 * T1809 — preklop v glavi je trajna izbira, ne le trenutni pogled (odločitev
 * Alena, 25.9.2026). `user.language` krmili jezik izvoza (`exports.py`),
 * e-pošte (T1389) in obvestil, zato brez tega nemški uporabnik po preklopu
 * izvozi slovenski popis. Pot je ista kot v profilu (`PATCH auth/me/`).
 *
 * Vmesnik se preklopi takoj; zahtevek ne blokira. Ob neuspehu jezik vmesnika
 * ostane, shramba pa ne dobi nove vrednosti — profil zato še naprej pravilno
 * kaže, kar je v bazi. Neprijavljen obiskovalec (landing, prijava, deljena
 * povezava) zahtevka ne pošlje.
 *
 * Obratna smer (T3038, odločitev Alena 14): ob prijavi in ob nalaganju z
 * veljavno sejo vmesnik prevzame `user.language` (`authStore.ts`
 * `applyAccountLanguage`). Zaznava v `i18n.ts` (localStorage → piškotek →
 * brskalnik) velja le za neprijavljene, zato je jezik računa en sam na vseh
 * napravah.
 */
async function persistLanguage(code: string): Promise<void> {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || !user) return;
  if ((_pendingLanguage ?? user.language) === code) return;
  const seq = ++_persistSeq;
  _pendingLanguage = code;
  try {
    const response = await api.patch('auth/me/', { language: code });
    if (seq !== _persistSeq) return;
    const current = useAuthStore.getState().user;
    if (!current) return;
    const saved = (response?.data as { language?: string } | undefined)?.language ?? code;
    useAuthStore.setState({ user: { ...current, language: saved } });
  } catch (err) {
    // Tiho: zaledje jezik zavrne samo, če ni med petimi jeziki računa (T2066),
    // preklopnik pa ponuja podmnožico teh (`supported_languages`) — neuspeh je
    // torej omrežni ali seja je potekla.
    console.warn('Language preference was not saved', err);
  } finally {
    if (seq === _persistSeq) _pendingLanguage = null;
  }
}

/**
 * T2885 — po koliko ms brez naloženega jezika preklopnik pokaže, da nalaga.
 * Krajše čakanje (predpomnjen kos) se ne opazi in ne utripa.
 */
export const LANGUAGE_LOADING_INDICATOR_DELAY_MS = 300;

interface LanguageSwitcherProps {
  /**
   * Opisuje PODLAGO, na kateri gumb stoji, ne teme:
   *  - `dark`    — temna glava aplikacije (`--bp-chrome`); belo besedilo,
   *  - `light`   — svetla podlaga s slate barvami (ima svoje `dark:` različice),
   *  - `toolbar` — orodna vrstica na `--bp-surface` (T1284): barve iz `--bp-*`
   *    tokenov, zato deluje v OBEH temah, in ista višina/radij kot sosednji
   *    gumbi. Privzetek `dark` je na svetli podlagi belo na belem.
   */
  variant?: 'light' | 'dark' | 'toolbar';
  /** Oznaka za teste; gumb je edini element komponente. */
  testId?: string;
}

export default function LanguageSwitcher({
  variant = 'dark',
  testId,
}: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const activeLanguages = useActiveLanguages();

  const uiLang = i18n.language ?? '';
  // T2702: gumb kaže DEJANSKI jezik vmesnika. Jezik profila (T2066) je lahko
  // zunaj `supported_languages` platforme (npr. `de` ob privzetem `['sl', 'en']`);
  // prej je gumb tedaj padel na prvi aktivni jezik in kazal »SL« nad nemščino.
  // T2885: med nalaganjem novega jezika je to še stari jezik (vmesnik kaže stara
  // besedila, dokler sveženj ni naložen) — gumb kaže starega.
  const shownLang = (uiLang || activeLanguages[0] || '').slice(0, 2).toUpperCase();

  // T2885: diskreten indikator, če nalaganje jezika traja dlje od praga.
  const [loading, setLoading] = useState(false);
  const pendingSeq = useRef(0);
  const indicatorTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(indicatorTimer.current), []);

  const nextLanguage = () => {
    // T2885: kroži od jezika, ki se morda že nalaga (hiter dvojni klik med
    // nalaganjem), ne od še prikazanega — prebrano ob klicu, ne ob izrisu.
    // `isLanguageChangingTo` je javno polje i18next, a ga tipi ne naštejejo.
    const changingTo = (i18n as { isLanguageChangingTo?: string }).isLanguageChangingTo;
    const target = changingTo || i18n.language || '';
    const currentLang = activeLanguages.find((l) => target.startsWith(l));
    // Jezik zunaj seznama (`-1`) preide na prvi aktivni jezik, sicer kroži.
    const idx = currentLang ? activeLanguages.indexOf(currentLang) : -1;
    return activeLanguages[(idx + 1) % activeLanguages.length];
  };

  // T2885: prevodi so lenobni kosi — ob hoverju/fokusu/pritisku naloži jezik,
  // ki ga bo klik izbral, da je ob kliku že v predpomnilniku.
  const preloadNext = () => {
    const next = nextLanguage();
    if (next) void i18n.loadLanguages?.(next)?.catch?.(() => undefined);
  };

  const handleClick = () => {
    const next = nextLanguage();
    const seq = ++pendingSeq.current;
    clearTimeout(indicatorTimer.current);
    indicatorTimer.current = setTimeout(() => {
      if (seq === pendingSeq.current) setLoading(true);
    }, LANGUAGE_LOADING_INDICATOR_DELAY_MS);
    const finish = () => {
      if (seq !== pendingSeq.current) return;
      clearTimeout(indicatorTimer.current);
      setLoading(false);
    };
    Promise.resolve(i18n.changeLanguage(next)).then(finish, finish);
    void persistLanguage(next);
  };

  return (
    <button
      onClick={handleClick}
      onMouseEnter={preloadNext}
      onFocus={preloadNext}
      onPointerDown={preloadNext}
      data-testid={testId}
      aria-busy={loading || undefined}
      className={cn(
        "font-bold tracking-widest border transition-all uppercase",
        variant === 'toolbar'
          ? "h-8 px-2.5 text-dense rounded-lg bg-[var(--bp-surface-2)] border-[var(--bp-border-soft)] text-[var(--bp-text-2)] hover:bg-[var(--bp-panel)] hover:text-[var(--bp-text-1)] cursor-pointer"
          : "px-2 py-1 text-caption rounded-sm",
        variant === 'dark' &&
          "border-white/20 text-white/70 hover:text-white hover:border-white/40",
        variant === 'light' &&
          "border-[var(--bp-border)] text-muted-foreground hover:text-[var(--bp-text-1)] hover:border-[var(--bp-mute)]",
        loading && "animate-pulse opacity-70 cursor-progress"
      )}
      title={t('common.switch_language')}
    >
      {shownLang}
    </button>
  );
}
